import React, { useState, useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import { Popup } from '@atlaskit/popup';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Pressable, xcss } from '@atlaskit/primitives';

import {
	LoomScriptReadTimeButtonNudgeWrapper,
	LoomScriptCrossFlowContextProvider,
} from '@confluence/experiment-loom-recording-suggestions-on-confluence-pages-using-llms';
import {
	useLoomScriptReadTimeContext,
	LoomScriptReadTimeContextProvider,
} from '@confluence/experiment-loom-recording-suggestions-on-confluence-pages-using-llms/entry-points/LoomScriptReadTimeContext';

import { ReadTimeComponent } from './ReadTimeComponent';
import { ReadTimePopupWithErrorBoundary as ReadTimePopup } from './ReadTimePopup';
import type { ContentFeatures } from './featuresExtractor';

const triggerStyles = xcss({
	display: 'inline-flex',
	color: 'color.text',
	padding: 'space.0',
	borderRadius: 'border.radius.100',
	backgroundColor: 'color.background.neutral.subtle',
});

interface ReadTimePopupExperimentProps {
	contentId: string;
	isAbbreviatedReadTime?: boolean;
	isDetailsPanel?: boolean;
	showWordCount?: boolean;
	features: ContentFeatures | null;
	featuresSkipExpand: ContentFeatures | null;
	wordCount: number;
	readTime: number | null;
	source: string;
	isEligibleForLoomAIScriptSection?: boolean;
}

const ReadTimePopupExperimentWithLoomScriptContext = ({
	contentId,
	isAbbreviatedReadTime,
	isDetailsPanel,
	showWordCount,
	features,
	featuresSkipExpand,
	wordCount,
	readTime,
	source,
	isEligibleForLoomAIScriptSection = false,
}: ReadTimePopupExperimentProps) => {
	const [isPopupOpen, setIsPopupOpen] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isLoomScriptReadTimeSpotlightShown } = useLoomScriptReadTimeContext();

	const onPageReadTimeClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'readTime',
				actionSubjectId: 'readTimePopup',
				source,
				attributes: {
					contentId,
				},
			},
		}).fire();
		setIsPopupOpen(!isPopupOpen);
	}, [isPopupOpen, createAnalyticsEvent, contentId, source]);

	if (isEligibleForLoomAIScriptSection) {
		return (
			<LoomScriptCrossFlowContextProvider>
				<Popup
					isOpen={isPopupOpen}
					onClose={() => {
						if (isLoomScriptReadTimeSpotlightShown) {
							// disable closing the popup when spotlight is shown
							return;
						}
						setIsPopupOpen(false);
					}}
					placement="bottom-start"
					content={(props) => (
						<ReadTimePopup
							{...props}
							features={features}
							featuresSkipExpand={featuresSkipExpand}
							source={source}
							isEligibleForLoomAIScriptSection={isEligibleForLoomAIScriptSection}
							contentId={contentId}
						/>
					)}
					trigger={(triggerProps) => (
						<LoomScriptReadTimeButtonNudgeWrapper>
							<Pressable {...triggerProps} onClick={onPageReadTimeClick} xcss={triggerStyles}>
								<ReadTimeComponent
									readTime={readTime}
									wordCount={wordCount}
									pageId={contentId}
									isAbbreviatedReadTime={isAbbreviatedReadTime}
									isDetailsPanel={isDetailsPanel}
									showWordCount={showWordCount}
								/>
							</Pressable>
						</LoomScriptReadTimeButtonNudgeWrapper>
					)}
				/>
			</LoomScriptCrossFlowContextProvider>
		);
	}

	return (
		<Popup
			isOpen={isPopupOpen}
			onClose={() => setIsPopupOpen(false)}
			placement="bottom-start"
			content={(props) => (
				<ReadTimePopup
					{...props}
					features={features}
					featuresSkipExpand={featuresSkipExpand}
					source={source}
					isEligibleForLoomAIScriptSection={isEligibleForLoomAIScriptSection}
					contentId={contentId}
				/>
			)}
			trigger={(triggerProps) => (
				<Pressable {...triggerProps} onClick={onPageReadTimeClick} xcss={triggerStyles}>
					<ReadTimeComponent
						readTime={readTime}
						wordCount={wordCount}
						pageId={contentId}
						isAbbreviatedReadTime={isAbbreviatedReadTime}
						isDetailsPanel={isDetailsPanel}
						showWordCount={showWordCount}
					/>
				</Pressable>
			)}
		/>
	);
};

export const ReadTimePopupExperiment = (props: ReadTimePopupExperimentProps) => (
	<LoomScriptReadTimeContextProvider
		isEligibleForLoomAIScriptSection={props.isEligibleForLoomAIScriptSection ?? false}
	>
		<ReadTimePopupExperimentWithLoomScriptContext {...props} />
	</LoomScriptReadTimeContextProvider>
);
